import { HttpParams } from '@angular/common/http';
import { AbstractType, Type } from '@angular/core';
import { ApiError, ApiProgressSubject, ApiReplaySubject, ApiSubject, BaseViewComponent, BodyRequired, CancellableObservable, Column, DialogEvents, Enum, ExtraQueryParametersOptional, IApiRequest, IRequestOptions, OneParameterRequired, OrderByOptional, PaginatorDataOptional, ProgressDialogData, ProgressKind, RequestKind, RequestedFieldsOptional, SortDirection, ThreeParametersRequired, TwoParametersRequired } from '@sb/library/base';
import { Arrayify, DisplayItemCollection } from '@sb/library/data-models';
import { DialogProgressComponent } from '@sb/library/dialog';
import { ReplaySubject, Subject, forkJoin, map, take, timer } from 'rxjs';
import { BaseApiRequestManager } from './base-api-request.manager';

export class ApiRoute<Request extends IRequestOptions, Result> {

    private static _apiRequestManager?: BaseApiRequestManager;

    private static readonly openProgressDialogAfterMs = 2500;
    private static readonly keepProgressDialogOpenForMs = 2500;

    static readonly ProgressCancelled = {
        Error: 'Cancelled',
        Message: 'User cancelled operation'
    };

    static Define<URLType extends URL,
                  Kind extends RequestKind,
                  Body extends AbstractType<any> | undefined = undefined,
                  OrderBy extends AbstractType<any> | undefined = undefined,
                  Fields extends AbstractType<any> | undefined = undefined,
                  ExtraParameters extends string | undefined = undefined,
                  Paging extends boolean = false,
                  Result extends ResultType = 'boolean'>(definition: {
            URL: URLType,
            Kind: Kind,
            BodyType?: BodyAllowed<Body, Kind>,
            OrderByType?: OrderBy,
            RequestedFieldsType?: Fields,
            PagingAllowed?: BooleanType<Paging>,
            ExtraParameters?: StringType<ExtraParameters> | StringType<ExtraParameters>[],
            ResultType?: Result,
            Cancellable?: boolean,
            ShowProgress?: ProgressKind
        }): ApiRoute<RequestTypeMap<URLType, Body, OrderBy, Fields, ExtraParameters, Paging, Result>, ResultTypeMap<Result>> {

        return new ApiRoute(definition.URL, definition.Kind, definition.ResultType, definition.Cancellable, definition.ShowProgress);
    }

    static SetApiRequestManager(manager: BaseApiRequestManager) {
        this._apiRequestManager = manager;
    }

    Kind!: RequestKind;
    URL!: URLType<Request>;
    ResultType: any;         // Only used to improve the heuristic for the default value of ignoreTransitionAnimations
    Cancellable!: boolean;
    ShowProgress!: ProgressKind;

    readonly Error!: ApiError<Request>;   // Used to type API errors

    private constructor(url: URL, kind: RequestKind, resultType: any, cancellable?: boolean, showProgress?: ProgressKind) {
        this.URL = url as URLType<Request>;
        this.Kind = kind;
        this.ResultType = resultType;
        this.Cancellable = cancellable ?? true;
        this.ShowProgress = showProgress ?? ProgressKind.None;
    }

    Call(requestOrVoid: EmptyToVoid<Request>, ignoreTransitionAnimations?: boolean): CancellableObservable<Result> {
        return this.CallWithSubject(requestOrVoid, undefined, undefined, ignoreTransitionAnimations);
    }

    CallWithSubject(requestOrVoid: EmptyToVoid<Request>, subject?: ApiSubject<Result, Request>, progressSubject?: ApiProgressSubject<Request>, ignoreTransitionAnimations: boolean = false): CancellableObservable<Result> {
        let request = (requestOrVoid ?? {}) as Request;
        subject ??= new Subject();

        const apiRequest: IApiRequest<Body> = {
            URL: this.MakeURL(request, this.URL),
            Kind: this.Kind,
            Body: request.Body,
            HttpParams: this.MakeHttpParams(request)
        };

        if (!ApiRoute._apiRequestManager) {
            throw Error('ApiRoute must have its ApiRequestManager set before a request can be made.');
        }

        const progress = this.SetupProgress(progressSubject);

        const cancelCallback = ApiRoute._apiRequestManager.Request(apiRequest, subject, progress.RequestProgressSubject, request, ignoreTransitionAnimations, this.Cancellable);

        // Return a one-off observable. Point being: if the return value of Call is used, no need to unsubscribe.
        // (We strip out the tracking information from this one-off observable, since it is unnecessary.)
        let result = subject.pipe(
            map(tracked => tracked.Response),
            take(1)) as CancellableObservable<Result>;

        result.Cancel = cancelCallback;

        if (progress.DialogProgressSubject) {
            result.Progress = progress.DialogProgressSubject;
            this.ShowProgressDialog(progress, result, subject, cancelCallback);
        }

        return result;
    }

    MakeSubject(): ApiSubject<Result, Request> {
        return new Subject();
    }

    MakeReplaySubject(): ApiReplaySubject<Result, Request> {
        return new ReplaySubject();
    }

    MakeProgressSubject(): ApiProgressSubject<Request> {
        return new Subject();
    }

    private MakeURL(request: Request, url: URLType<Request>): string {
        if (IsURLWithNoParameters(url)) {
            return url;
        } else if (IsURLWithOneParameter(url)) {
            return url(request.Parameter!);
        } else if (IsURLWithTwoParameters(url)) {
            return url(request.Parameter1!, request.Parameter2!);
        } else if (IsURLWithThreeParameters(url)) {
            return url(request.Parameter1!, request.Parameter2!, request.Parameter3!);
        } else {
            throw Error('More than 3 URL parameters not implemented');
        }
    }

    private MakeHttpParams(request: Request): HttpParams {
        let httpParams = new HttpParams();

        const paginatorData = request.PaginatorData;
        if (paginatorData) {
            httpParams = httpParams.append('offset', paginatorData.PageNumber * paginatorData.PageSize + (paginatorData.OffsetAdjustment ?? 0));
            httpParams = httpParams.append('limit', paginatorData.PageSize);
        }

        if (request.RequestedFields && request.RequestedFields.length) {
            let optionalFieldsArray: string[] = [];

            request.RequestedFields.forEach(field => {
                let fieldString: string;
                if (Column.IsSynthetic(field.Field)) {
                    if (field.Field.RequestBy === undefined) {
                        console.error(`Cannot request synthetic column ${(field.Field.Identifier)} as it does not have a RequestBy provided.`);
                        return;
                    }
                    fieldString = field.Field.RequestBy;
                } else {
                    fieldString = field.Field;
                }

                if (field.Value !== undefined) {
                    fieldString += `=${field.Value}`;
                }

                optionalFieldsArray.push(fieldString);
            });

            httpParams = httpParams.append('fields', optionalFieldsArray.join(','));
        }

        if (request.OrderBy && request.OrderBy.SortColumns.length) {
            let orderByArray: string[] = [];

            request.OrderBy.SortColumns.forEach(sortColumn => {
                let orderByStrings: string[];
                if (Column.IsSynthetic(sortColumn.Column)) {
                    if (!sortColumn.Column.SortBy) {
                        throw new Error(`No SortBy provided for synthetic column ${sortColumn.Column.Identifier}`)
                    }
                    orderByStrings = Arrayify(sortColumn.Column.SortBy);
                } else {
                    orderByStrings = [sortColumn.Column];
                }

                orderByStrings.forEach(orderByString => {
                    if (orderByString.startsWith('~')) {    // Invariant to overall order
                        orderByArray.push(orderByString.substring(1));
                    } else if (sortColumn.Direction == SortDirection.Ascending) {
                        orderByArray.push(orderByString);
                    } else if (sortColumn.Direction == SortDirection.Descending) {
                        if (orderByString.startsWith('-')) {
                            orderByArray.push(orderByString.substring(1));
                        } else {
                            orderByArray.push('-' + orderByString);
                        }
                    }
                });
            });

            httpParams = httpParams.append('orderby', orderByArray.join(','));
        }

        if (request.ExtraQueryParameters) {
            for (const property in request.ExtraQueryParameters) {
                const value = request.ExtraQueryParameters[property];
                if (value) {
                    httpParams = httpParams.append(property, value);
                }
            }
        }

        return httpParams;
    }

    private SetupProgress(progressSubject: ApiProgressSubject<Request> | undefined): ApiRouteProgress<Request> {
        let progressDialogSubject: ReplaySubject<number> | undefined;

        if (this.ShowProgress !== ProgressKind.None) {
            progressDialogSubject = new ReplaySubject<number>(1);
            progressSubject ??= this.MakeProgressSubject();
            progressSubject.subscribe(event => {
                progressDialogSubject!.next(Math.round(100 * event.Response.loaded / event.Response.total!));
            });
            progressSubject.Computation = this.ShowProgress === ProgressKind.Computation;
            progressSubject.Upload = this.ShowProgress === ProgressKind.Upload;
        } else if (progressSubject) {  // TODO: Grandfathered in upload progress without route change. Update routes to all use ShowProgress approach instead.
            progressSubject.Upload = true;
        }

        return {
            RequestProgressSubject: progressSubject,
            DialogProgressSubject: progressDialogSubject
        };
    }

    private ShowProgressDialog(progress: ApiRouteProgress<Request>, result: CancellableObservable<Result>, subject: ApiSubject<Result, Request>, cancelCallback: () => void): void {
        let dialogComponent: DialogProgressComponent | undefined;
        let isComplete = false;

        const dialogData = new ProgressDialogData({ ProgressObservable: progress.DialogProgressSubject });

        const delayedDismiss = () => timer(500).subscribe(_ => dialogComponent?.DismissAction());
        const onComplete = () => {
            if (!isComplete) {
                isComplete = true;
                progress.DialogProgressSubject!.complete();

                if (!dialogComponent) {
                    dialogOpenSubscription.unsubscribe();
                    dialogDismissSubscription.unsubscribe();
                }
            }
        }

        const dialogDismissSubscription = forkJoin([timer(ApiRoute.openProgressDialogAfterMs + ApiRoute.keepProgressDialogOpenForMs), result]).subscribe({
            next: delayedDismiss,
            error: delayedDismiss
        });

        const dialogOpenSubscription = timer(ApiRoute.openProgressDialogAfterMs).subscribe(() => {
            dialogComponent = BaseViewComponent['_DialogManager'].CreateDialog(DialogProgressComponent, dialogData, null);
            dialogComponent!.events.subscribe(event => {
                if (event === DialogEvents.PrimaryAction) {
                    cancelCallback();
                    subject.error(ApiRoute.ProgressCancelled);
                } else if (event === DialogEvents.Dismissed) {
                    subject.complete();
                }
            });

        });

        result.subscribe({
            error: onComplete,
            complete: onComplete
        });
    }
}

// The following types are used only in the definitions above

type RequestTypeMap<URLType, Body, OrderBy, Fields, ExtraParameters, Paging, Result> =
    RemoveEmpty<
        URLTypeMap<URLType> &
        BodyTypeMap<Body> &
        OrderByRequestTypeMap<OrderBy, Result> &
        FieldsRequestTypeMap<Fields, Result> &
        ExtraParametersTypeMap<ExtraParameters> &
        PagingTypeMap<Paging>
    >;

// Used to allow empty Call() on routes with no required parameters
type EmptyToVoid<T> = {} extends T ? void | T: T;
type Empty = Record<PropertyKey, never>;
type RemoveEmpty<T> = keyof T extends never ? Empty : T;

export type URLWithNoParameters = string;
export type URLWithOneParameter = (param: string) => string;
export type URLWithTwoParameters = (param1: string, param2: string) => string;
export type URLWithThreeParameters = (param1: string, param2: string, param3: string) => string;
export type URL = URLWithNoParameters | URLWithOneParameter | URLWithTwoParameters | URLWithThreeParameters;

// Force T to be either true or false (but not boolean)
type BooleanType<T> = [T] extends [boolean] ? [boolean] extends [T] ? never : T : never;
type StringType<T> = [T] extends [string] ? [string] extends [T] ? never : T : never;
type ResultType = 'string' | 'number' | 'boolean' | Enum<any> | AbstractType<any>
type BodyAllowed<T, Kind> = Kind extends RequestKind.Get ? never : T;
type URLType<T extends IRequestOptions> = {} extends T ? URLWithNoParameters
                                        : OneParameterRequired extends T ? URLWithOneParameter
                                        : TwoParametersRequired extends T ? URLWithTwoParameters
                                        : ThreeParametersRequired extends T ? URLWithThreeParameters
                                        : never;

type URLTypeMap<T> = T extends URLWithOneParameter ? OneParameterRequired
                   : T extends URLWithTwoParameters ? TwoParametersRequired
                   : T extends URLWithThreeParameters ? ThreeParametersRequired
                   : {};
type BodyTypeMap<T> = [T] extends [undefined] ? {} : T extends AbstractType<infer U> ? BodyRequired<U> : never;
type PagingTypeMap<T> = T extends true ? PaginatorDataOptional : {};
type ExtraParametersTypeMap<T> = [T] extends [undefined] ? {}
                               : T extends string ? ExtraQueryParametersOptional<NonNullable<T>>
                               : never;

type ResultTypeMap<T> = T extends 'string' ? string
                      : T extends 'number' ? number
                      : T extends 'boolean' ? boolean
                      : T extends Type<infer U> ? U
                      : T extends AbstractType<infer U> ? U
                      : T[keyof T];

type FieldsRequestTypeMap<T, U> = [T] extends [undefined] ? U extends Type<DisplayItemCollection<infer V>> ? RequestedFieldsOptional<V>
                                                          : U extends Type<Array<infer V>> ? RequestedFieldsOptional<V>
                                                          : U extends Type<infer V> ? RequestedFieldsOptional<V>
                                                          : {}
                                : T extends Type<infer U> ? RequestedFieldsOptional<U>
                                : never;
type OrderByRequestTypeMap<T, U> = [T] extends [undefined] ? U extends Type<DisplayItemCollection<infer V>> ? OrderByOptional<V>
                                                           : U extends Type<Array<infer V>> ? OrderByOptional<V>
                                                           : {}
                                 : T extends Type<infer U> ? OrderByOptional<U>
                                 : never;

export function IsURLWithNoParameters(url: any): url is URLWithNoParameters {
    return typeof url == 'string';
}
export function IsURLWithOneParameter(url: any): url is URLWithOneParameter {
    return typeof url == 'function' && url.length == 1;
}
export function IsURLWithTwoParameters(url: any): url is URLWithTwoParameters {
    return typeof url == 'function' && url.length == 2;
}
export function IsURLWithThreeParameters(url: any): url is URLWithThreeParameters {
    return typeof url == 'function' && url.length == 3;
}

interface ApiRouteProgress<Request extends IRequestOptions> {
    RequestProgressSubject?: ApiProgressSubject<Request>;
    DialogProgressSubject?: ReplaySubject<number>;
}
